import { CSSProperties, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';

import { AI_CHAT_MODELS } from '@atom/graph/ai';
import { Icon, IconButton, Popover } from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { AIChatModels } from '@atom/types/ai';

import { useChatContext } from './ChatContext';

import classes from './chat.module.css';

const styles: Record<string, CSSProperties> = {
  icon: {
    fontSize: fonts.md,
  },
  checkIcon: {
    fontSize: fonts.sm,
    marginRight: '0.5rem',
  },
  checkPlaceholder: {
    width: fonts.sm,
    marginRight: '0.5rem',
  },
};

const ChatModelSelection = () => {
  const {
    modelSelectionData: { model, scope, setModel, setScope, clear },
  } = useChatContext();

  const { data } = useQuery<{ aiChatModels: AIChatModels }>(AI_CHAT_MODELS);

  const anchor = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const models = data?.aiChatModels?.models ?? [];
  const defaultModel = data?.aiChatModels?.default;
  const defaultLabel = defaultModel
    ? `Default (${defaultModel.name})`
    : 'Default';

  const isOverriding = model !== null;
  const iconColor = isOverriding ? colors.brand.blue : colors.neutral.gray;

  const selectedName = models.find(({ id }) => id === model)?.name ?? model;

  const handleIconClick = () => {
    setOpen(prev => !prev);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleScopeSelect = (value: 'agent' | 'all') => {
    setScope(value);
  };

  const handleModelSelect = (modelId: string | null) => {
    if (modelId === null) {
      clear();
    } else {
      setModel(modelId);
    }
    setOpen(false);
  };

  const tooltip = isOverriding ? `Model: ${selectedName}` : 'Select Model';

  return (
    <div ref={anchor}>
      <IconButton onClick={handleIconClick} size="small" tooltip={tooltip}>
        <Icon style={styles.icon} color={iconColor}>
          tune
        </Icon>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchor.current as any}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        onClose={handleClose}
      >
        <div className={classes.modelSelectionPopover}>
          <div className={classes.modelSelectionSection}>
            <div className={classes.modelSelectionSectionLabel}>Scope</div>
            <div
              className={classes.modelSelectionRow}
              onClick={() => handleScopeSelect('agent')}
            >
              {scope === 'agent' ? (
                <Icon style={styles.checkIcon} color={colors.brand.blue}>
                  check
                </Icon>
              ) : (
                <span style={styles.checkPlaceholder} />
              )}
              Agent Only
            </div>
            <div
              className={classes.modelSelectionRow}
              onClick={() => handleScopeSelect('all')}
            >
              {scope === 'all' ? (
                <Icon style={styles.checkIcon} color={colors.brand.blue}>
                  check
                </Icon>
              ) : (
                <span style={styles.checkPlaceholder} />
              )}
              All Inference
            </div>
          </div>
          <div className={classes.modelSelectionDivider} />
          <div className={classes.modelSelectionSection}>
            <div className={classes.modelSelectionSectionLabel}>Model</div>
            <div
              className={classes.modelSelectionRow}
              onClick={() => handleModelSelect(null)}
            >
              {model === null ? (
                <Icon style={styles.checkIcon} color={colors.brand.blue}>
                  check
                </Icon>
              ) : (
                <span style={styles.checkPlaceholder} />
              )}
              {defaultLabel}
            </div>
            {models.map(({ id, name }) => (
              <div
                key={id}
                className={classes.modelSelectionRow}
                onClick={() => handleModelSelect(id)}
              >
                {model === id ? (
                  <Icon style={styles.checkIcon} color={colors.brand.blue}>
                    check
                  </Icon>
                ) : (
                  <span style={styles.checkPlaceholder} />
                )}
                {name}
              </div>
            ))}
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default ChatModelSelection;
